/**
 * CASDM phase vocabulary — single source of truth for phase numbers, names and display labels.
 *
 * The API reports `current_phase` as "Phase N". Older weekly reports stored free-text labels such
 * as "P3 — Build & Implement" / "P0 — Internal Prep"; `phaseNumber()` understands those too so the
 * UI can render every stored value with the canonical label.
 */

/** Canonical CASDM phase names, indexed by phase number (0..4). */
export const PHASE_NAMES = [
  "Internal Preparation",
  "Discover & Align",
  "Design & Review",
  "Build & Implement",
  "Launch & Enable",
] as const;

export type PhaseName = (typeof PHASE_NAMES)[number];

const MAX_PHASE = PHASE_NAMES.length - 1;

/**
 * Leading phase token: "Phase 3", "phase3", "P3", "p 3" or a bare "3", optionally followed by a
 * separator and a trailing name ("P3 — Build & Implement", "Phase 0 - Internal Prep").
 */
const PHASE_RE = /^(?:phase|p)?\s*(\d+)(?:\s*(?:[—–\-:·|]|$).*)?$/i;

/** Parse a phase reference to its number (0..4), or null when it isn't a recognisable phase. */
export function phaseNumber(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 && value <= MAX_PHASE
      ? value
      : null;
  }
  const match = PHASE_RE.exec(value.trim());
  if (!match) return null;
  const n = Number(match[1]);
  return n >= 0 && n <= MAX_PHASE ? n : null;
}

/** Canonical phase name ("Build & Implement"), or null when unparseable. */
export function phaseName(
  value: string | number | null | undefined,
): PhaseName | null {
  const n = phaseNumber(value);
  return n === null ? null : PHASE_NAMES[n];
}

/**
 * Display label "Phase 3 — Build & Implement". Falls back to the trimmed input when it can't be
 * parsed, and to "—" when empty.
 */
export function phaseLabel(value: string | number | null | undefined): string {
  const n = phaseNumber(value);
  if (n !== null) return `Phase ${n} — ${PHASE_NAMES[n]}`;
  const raw = value === null || value === undefined ? "" : String(value).trim();
  return raw || "—";
}
